/**
 * useUndoManager - Global undo action manager
 * 
 * Provides a centralized way to manage undo actions across the app
 * with toast notifications
 */

import { create } from 'zustand';

export interface UndoAction {
  id: string;
  message: string;
  onUndo: () => void | Promise<void>;
  variant?: 'success' | 'warning' | 'error' | 'info';
  timeout?: number;
}

interface UndoStore {
  actions: UndoAction[];
  addAction: (action: Omit<UndoAction, 'id'>) => string;
  removeAction: (id: string) => void;
  executeUndo: (id: string) => Promise<void>;
}

export const useUndoStore = create<UndoStore>((set, get) => ({
  actions: [],
  
  addAction: (action) => {
    const id = `undo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newAction: UndoAction = { ...action, id };
    
    set((state) => ({
      actions: [...state.actions, newAction],
    }));
    
    return id;
  },
  
  removeAction: (id) => {
    set((state) => ({
      actions: state.actions.filter((a) => a.id !== id),
    }));
  },
  
  executeUndo: async (id) => {
    const action = get().actions.find((a) => a.id === id);
    if (action) {
      await action.onUndo();
      get().removeAction(id);
    }
  },
}));

/**
 * Hook to use the undo manager
 */
export function useUndoManager() {
  const { addAction, removeAction, executeUndo } = useUndoStore();
  
  /**
   * Register an undoable action
   * Returns the action ID
   */
  const registerUndo = (
    message: string,
    onUndo: () => void | Promise<void>,
    options?: {
      variant?: 'success' | 'warning' | 'error' | 'info';
      timeout?: number;
    }
  ): string => {
    return addAction({
      message,
      onUndo,
      variant: options?.variant || 'success',
      timeout: options?.timeout || 8000,
    });
  };
  
  /**
   * Cancel an undo action (removes it from the queue)
   */
  const cancelUndo = (actionId: string) => {
    removeAction(actionId);
  };
  
  /**
   * Execute an undo action
   */
  const undo = async (actionId: string) => {
    await executeUndo(actionId);
  };
  
  return {
    registerUndo,
    cancelUndo,
    undo,
  };
}

